/**
 * Wraps a row of filter controls (FilterSelect / inputs / etc.) with
 * the shared layout: flex-wrap container, optional "clear" button on
 * the right of the controls, and a right-aligned "X of Y" counter.
 *
 * Pulls out the duplicated `<div className="flex flex-wrap gap-3 ..."`
 * pattern from constructions, skills, admin.users, admin.reports.
 *
 * Pair with <FilterEmpty /> when the filtered list is empty.
 */

import type { ReactNode } from "react";

type FilterBarProps = {
  children: ReactNode;
  /** Render the clear-filters link when provided (and any filters are active). */
  onClear?: (() => void) | undefined;
  /** Total visible after filtering. Shown as "X of Y" when `total` is set. */
  shown?: number;
  /** Unfiltered total. Required if you want the counter. */
  total?: number;
};

export function FilterBar({ children, onClear, shown, total }: FilterBarProps) {
  const showCounter = typeof shown === "number" && typeof total === "number";

  return (
    <div className="flex flex-wrap gap-3 mb-4 items-end">
      {children}
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-ink-500 underline underline-offset-2 hover:text-ink-900"
        >
          clear
        </button>
      )}
      {showCounter && (
        <span className="ml-auto text-xs text-ink-400 tabular-nums">
          {shown} of {total}
        </span>
      )}
    </div>
  );
}

/**
 * Shared empty-state panel for filtered-to-zero lists.
 * Sits below the FilterBar in the same section.
 */
export function FilterEmpty({ noun = "items" }: { noun?: string }) {
  return (
    <div className="text-ink-500 text-sm py-12 text-center border border-dashed border-ink-200">
      No {noun} match your filters.
    </div>
  );
}
